import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import copy from "clipboard-copy";
import { BarChart } from "components/Dashboard/BarChart";
import { AmazonWadmTotals } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatNumber } from "util/formatNumber";
import { PriorityChartColorsPurple } from "util/insights";

const CHART_MAX_PROFILES = 10;
const INITIAL_VISIBLE_PROFILES = 7;
const NO_PROFILES: AmazonWadmTotals["byProfile"] = [];

interface WadmTotalsProps {
  totals: AmazonWadmTotals | null | undefined;
  loading: boolean;
  onFilterByProfile: (profile: string) => void;
}

interface StatDisplayProps {
  label: string;
  value: string;
  unit?: string;
  rawValue: string;
  disabled: boolean;
}

function StatDisplay({ label, value, unit, rawValue, disabled }: StatDisplayProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    if (disabled) return;
    await copy(rawValue);
    setCopied(true);
  }, [disabled, rawValue]);

  const tooltipTitle = disabled ? "" : copied ? "Copied to clipboard" : "Click to copy";

  return (
    <Tooltip title={tooltipTitle} placement="bottom-start">
      <Stack
        py={1}
        justifyContent="flex-end"
        onClick={handleCopy}
        sx={{ cursor: disabled ? "default" : "pointer", userSelect: "none" }}
      >
        <Typography fontSize="0.75rem" fontWeight={400} color="text.secondary">
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            fontSize="2rem"
            fontWeight={400}
            color={disabled ? "text.disabled" : "text.primary"}
          >
            {value}
          </Typography>
          {unit && !disabled && (
            <Typography fontSize="0.75rem" fontWeight={400} color="text.secondary">
              {unit}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Tooltip>
  );
}

export function WadmTotals({ totals, loading, onFilterByProfile }: WadmTotalsProps) {
  const disabled = totals == null;
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState<{ idx: number; field: "weight" | "items" } | null>(null);

  useEffect(() => {
    setShowAll(false);
  }, [totals]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const allProfiles = totals?.byProfile ?? NO_PROFILES;

  const visibleProfiles = useMemo(
    () => (showAll ? allProfiles : allProfiles.slice(0, INITIAL_VISIBLE_PROFILES)),
    [allProfiles, showAll]
  );

  const chartDataset = useMemo(
    () =>
      allProfiles.slice(0, CHART_MAX_PROFILES).map((p) => ({
        name: p.wasteProfile ?? "(no profile)",
        amount: Math.round(p.totalWeightLbs),
      })),
    [allProfiles]
  );

  const hiddenCount = Math.max(0, allProfiles.length - INITIAL_VISIBLE_PROFILES);

  const paper = (
    <Paper elevation={0} sx={{ width: "100%", mb: 2, opacity: loading ? 0.5 : 1, px: 3, py: 2 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Summary
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 5 }} flexWrap="wrap">
        <StatDisplay
          label="Total weight"
          value={totals ? formatNumber(Math.round(totals.totalWeightLbs)) : "—"}
          unit="lbs"
          rawValue={totals ? String(Math.round(totals.totalWeightLbs)) : ""}
          disabled={disabled}
        />
        <StatDisplay
          label="Total items"
          value={totals ? formatNumber(totals.totalItemCount) : "—"}
          rawValue={totals ? String(totals.totalItemCount) : ""}
          disabled={disabled}
        />
      </Stack>

      {!disabled && allProfiles.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Highest generating profiles in lbs
          </Typography>
          <Stack direction="row" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, height: 260 }}>
              <BarChart
                dataset={chartDataset}
                series={[
                  {
                    type: "bar",
                    dataKey: "amount",
                    valueFormatter: (value) => ` ${formatNumber(value)} lbs`,
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "band",
                    dataKey: "name",
                    categoryGapRatio: 0.4,
                    colorMap: {
                      type: "ordinal",
                      colors: PriorityChartColorsPurple,
                    },
                  },
                ]}
                yAxis={[{ scaleType: "linear" }]}
                indexAxisLabelProperty="name"
              />
            </Box>
            <Stack sx={{ width: 260, flexShrink: 0, px: 1 }}>
              <Stack
                direction="row"
                gap={1}
                sx={{ pb: 0.5, mb: 0.25, borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Profile
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 80, textAlign: "right" }}
                >
                  Weight (lbs)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 44, textAlign: "right" }}
                >
                  Items
                </Typography>
              </Stack>
              {visibleProfiles.map((p, i) => {
                const rawWeight = Math.round(p.totalWeightLbs);
                const weightCopied = copied?.idx === i && copied.field === "weight";
                const itemsCopied = copied?.idx === i && copied.field === "items";
                const profileName = p.wasteProfile;

                return (
                  <Stack
                    key={profileName ?? String(i)}
                    direction="row"
                    gap={1}
                    alignItems="center"
                    sx={{ py: 0.25, borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        minWidth: 12,
                        borderRadius: "2px",
                        bgcolor: PriorityChartColorsPurple[i] ?? PriorityChartColorsPurple[0],
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip
                      title={profileName ? "Filter grid by this profile" : ""}
                      placement="left"
                      disableHoverListener={!profileName}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          flex: 1,
                          userSelect: "none",
                          ...(profileName && {
                            cursor: "pointer",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }),
                        }}
                        onClick={profileName ? () => onFilterByProfile(profileName) : undefined}
                      >
                        {i + 1}. {profileName ?? "(no profile)"}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={weightCopied ? "Copied!" : "Click to copy"} placement="left">
                      <Typography
                        variant="body2"
                        sx={{
                          width: 80,
                          textAlign: "right",
                          flexShrink: 0,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={async () => {
                          await copy(String(rawWeight));
                          setCopied({ idx: i, field: "weight" });
                        }}
                      >
                        {formatNumber(rawWeight)}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={itemsCopied ? "Copied!" : "Click to copy"} placement="left">
                      <Typography
                        variant="body2"
                        sx={{
                          width: 44,
                          textAlign: "right",
                          flexShrink: 0,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={async () => {
                          await copy(String(p.totalItemCount));
                          setCopied({ idx: i, field: "items" });
                        }}
                      >
                        {formatNumber(p.totalItemCount)}
                      </Typography>
                    </Tooltip>
                  </Stack>
                );
              })}
              {hiddenCount > 0 && (
                <Button
                  onClick={() => setShowAll(!showAll)}
                  variant="text"
                  size="small"
                  endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {showAll ? "Show less" : `Show ${hiddenCount} more`}
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      )}

      {disabled && (
        <Typography fontSize="0.75rem" color="text.disabled" sx={{ mt: 0.5 }}>
          Apply a search filter to view totals
        </Typography>
      )}
    </Paper>
  );

  return disabled ? (
    <Tooltip title="Apply a search filter to view totals" placement="bottom-start">
      <span>{paper}</span>
    </Tooltip>
  ) : (
    paper
  );
}
